import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";
import OrganizationService from "@/modules/Organization/services/organization.service";
import { ActionContext, ActionTree } from "vuex";
import { IOrganizationState } from "@/core/interfaces/organizationState.interface";
import type { TravelerProfileSummary } from "@/core/interfaces/organizationState.interface";
import { IPaginationMeta } from "@/core/interfaces/state/paginationMeta.interface";
import { UpdateOrganizationPreferenceRequest } from "../models/UpdateOrganizationPreferenceRequest";
import {
  AddWhatsappMemberDto,
  ContentUpload,
  OrganizationContentUpload,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  AuthorizeWhatsppMemberOTPDto,
  StartDateEndDateDTO,
} from "@/modules/Organization/dtos/organization.dto";
import { AUTH_EVENTS, authBus } from "@/core/eventBusses/auth";
import {
  AddAgentDto,
  BulkImportUsersRequest,
} from "@/modules/Organization/dtos/agent.dto";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString, stringEncrypt } from "@/core/utils/helpers";
import { convertCase } from "@/core/utils/string.utils";
import { ORGANIZATION_STATUSES } from "@/core/enums/ORGANIZATION_STATUSES";
import analyticsService from "@/analytic.service";
import {
  ORGANIZATION_ANALYTICS_EVENTS,
  CURRENCY_CONVERSION_ANALYTICS_EVENTS,
} from "@/modules/Organization/constants/analyticsEvents";
import { WHATSAPP_MEMBERS_PAGE_SIZE } from "@/modules/Organization/constants";
import AgentService from "@/modules/Agent/services/agent.service";
import { UpdateWebsiteConfigDto } from "../dtos/website.dto";
import { IWebsiteContent } from "@/core/interfaces/website.interface";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { OrganizationProductivityParamsRequestModel } from "../models/FetchOrganizationProductivityParams.request";
import ProfileAuthService from "@/modules/Profile/services/auth.service";
import type {
  UpdateUserProfileDto,
  VerifyDisableTwoFactorOtpDto,
} from "@/modules/Profile/dtos/auth.dto";
import { STORAGE_KEYS } from "@/core/constants/storageKeys";
import storageService from "@/core/services/storage.service";
import { StoreState } from "@/store/type";
import { ORGANIZATION_STATUS, Organization } from "@/modules/Auth/types";
import { UpdateFinancialProfileProviders } from "../models/UpdateFinancialProfileProviders.request";
import ManagePricingService from "../services/managePricing.service";
import { UpdatePricingDto } from "../dtos/managePricing.dto";
import {
  OrganizationPassengerSetupForm,
  OrganizationPassengerSetupFormV2,
} from "../models/OrganizationPassengerSetupForm.reponse";
import { DEPARTMENT_ANALYTICS_EVENTS } from "@/modules/Department/constants/analyticsEvents";
import { COST_CENTER_EVENTS } from "@/modules/CostCenter/constants/analyticsEvents";
import { EMPLOYEE_GRADE_ANALYTICS_EVENTS } from "@/modules/EmployeeGrade/constants/analyticsEvents";
import { IAgentDetails } from "@/core/interfaces/agent.interface";

let passengersSearchController: AbortController | undefined;
let travelerProfilesSearchController: AbortController | undefined;
let organizationUsersSearchController: AbortController | undefined;

const DEFAULT_TRAVELER_PROFILES_PAGE_SIZE = 20;

const actions: ActionTree<IOrganizationState, StoreState> = {
  async createOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      body,
      callback,
    }: { body: CreateOrganizationDto; callback: (id: string) => void },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("enableCreateOrganizationLoading");

      const organizationService = new OrganizationService();
      response = await organizationService.create(body);
      if (response.success && response.status === StatusCodes.OK) {
        callback(response.data.public_id);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.ORGANIZATION_CREATE_SUCCESS;
        notificationService.triggerNotification();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    context.commit("disableCreateOrganizationLoading");
  },
  async updateOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    { body, callback }: { body: UpdateOrganizationDto; callback: () => void },
  ) {
    let response: ICoreResponse = {
      success: false,
    };

    try {
      context.commit("enableUpdateOrganizationLoading");

      const organizationService = new OrganizationService();
      response = await organizationService.update(body);
      if (response.success && response.status === StatusCodes.OK) {
        callback();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.ORGANIZATION_UPDATE_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    context.commit("disableUpdateOrganizationLoading");
    notificationService.triggerNotification();
  },

  async deleteOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      organizationId,
      callback,
    }: { organizationId: string; callback: () => void },
  ) {
    let response: ICoreResponse<any> = {
      success: false,
    };

    try {
      context.commit("enableUpdateOrganizationLoading");

      const organizationService = new OrganizationService();
      response = await organizationService.delete(organizationId);
      if (response.success && response.status === StatusCodes.OK) {
        callback();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response?.message || "";
      } else {
        throw response;
      }
    } catch (exception: any) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    context.commit("disableUpdateOrganizationLoading");
    notificationService.triggerNotification();
  },

  async updateStatus(
    context: ActionContext<IOrganizationState, StoreState>,
    { body, callback }: { body: UpdateOrganizationDto; callback: () => void },
  ) {
    let response: ICoreResponse = { success: false };
    try {
      context.commit("enableUpdateOrganizationLoading");

      const organizationService = new OrganizationService();
      response = await organizationService.update(body);
      if (response.success && response.status === StatusCodes.OK) {
        callback();
        context.commit("updateStatus", response.data);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.ORGANIZATION_STATUS_UPDATE_SUCCESS,
          body.status === ORGANIZATION_STATUSES.ACTIVE
            ? "activated"
            : "deactivated",
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    notificationService.triggerNotification();
  },
  async fetchOrganizations(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      context.commit("enableFetchOrganizationsLoading");
      const organizationService = new OrganizationService();
      const response: ICoreResponse = await organizationService.getAll();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveOrganizations", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("saveOrganizations", []);
    }
  },
  async fetchOrganizationsByCreationDate(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: StartDateEndDateDTO,
  ) {
    context.commit("saveOrganizations", []);
    try {
      context.commit("enableFetchOrganizationsLoading");
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getAllByCreationDate(payload);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveOrganizations", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("saveOrganizations", []);
    }
  },
  async fetchOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    id: string,
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setErrorOrganizationNotFound", false);
      context.commit("enableFetchOrganizationLoading");
      const organizationService = new OrganizationService();
      response = await organizationService.getById(id, {
        financial_data: "show",
      });
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveOrganization", response.data);
      } else {
        context.commit("saveOrganization", null);
        context.commit("setErrorOrganizationNotFound", true);
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      context.commit("saveOrganization", null);
      context.commit("setErrorOrganizationNotFound", true);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("disableFetchOrganizationLoading");
    }
  },
  async updateOrganizationPreference(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
    }: { payload: UpdateOrganizationPreferenceRequest; organizationId: string },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      if (!organizationId) {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          NOTIFICATION_MESSAGES.ORGANIZATION_NOT_FOUND;
      }
      context.commit("enableUpdateOrganizationLoading");
      const organizationService = new OrganizationService();
      response = await organizationService.updatePreference(
        payload,
        organizationId,
      );
      context.commit("disableUpdateOrganizationLoading");
      if (response.success && response.status === StatusCodes.OK) {
        authBus.emit(AUTH_EVENTS.UPDATE_PREFERENCES, response.data);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.ORGANIZATION_UPDATE_PREFERENCE_SUCCESS;
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.UPDATE_ORGANIZATION_PREFERENCE,
          {
            displayName: payload.displayName,
            color: payload.defaultColor,
            isUpdateLogo: payload.logoFile ? true : false,
          },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      context.commit("disableUpdateOrganizationLoading");
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  },

  async addAgentInOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { body: AddAgentDto; orgId: string; callback: () => void },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("enableAddAgentLoading");
      const organizationService = new OrganizationService();
      response = await organizationService.createAgent(
        payload.body,
        payload.orgId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.ADD_AGENT_SUCCESS,
          convertCase(payload.body.role, "sentence"),
        );
        context.commit("updateAgents", response.data.organization_users);
        payload.callback();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      context.commit("disableAddAgentLoading");
    }

    notificationService.triggerNotification();
  },

  async validateBulkOrganizationUsers(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      body: BulkImportUsersRequest;
      orgId: string;
      callback?: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setIsValidatingBulkUsers", true);
      const organizationService = new OrganizationService();
      response = await organizationService.validateBulkUsers(
        payload.body,
        payload.orgId,
      );
      if (response.success) {
        context.commit("setBulkUserValidateResult", response.data ?? null);
        payload.callback?.();
      } else {
        context.commit(
          "setBulkUserValidateResult",
          response.errors ?? response,
        );
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      const hasRowErrors =
        Array.isArray(exception.errors?.rows) &&
        exception.errors.rows.length > 0;
      if (!hasRowErrors) {
        context.commit("setBulkUserValidateResult", null);
      }
      throw exception;
    } finally {
      context.commit("setIsValidatingBulkUsers", false);
    }
  },

  async importBulkOrganizationUsers(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      body: BulkImportUsersRequest;
      orgId: string;
      callback?: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setIsImportingBulkUsers", true);
      const organizationService = new OrganizationService();
      response = await organizationService.importBulkUsers(
        payload.body,
        payload.orgId,
      );
      if (response.success) {
        context.commit("setBulkUserImportResult", response.data ?? null);
        if (response.data?.organization_users) {
          context.commit("updateAgents", response.data.organization_users);
        }
        payload.callback?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      context.commit("setBulkUserImportResult", null);
      throw exception;
    } finally {
      context.commit("setIsImportingBulkUsers", false);
    }
  },

  async updateAgentInOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { body: AddAgentDto; orgId: string; callback: () => void },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("enableAddAgentLoading");
      const organizationService = new OrganizationService();
      response = await organizationService.createAgent(
        payload.body,
        payload.orgId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.UPDATE_AGENT_SUCCESS,
          convertCase(payload.body.role, "sentence"),
        );
        context.commit("updateAgents", response.data.organization_users);
        payload.callback();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      context.commit("disableAddAgentLoading");
    }

    notificationService.triggerNotification();
  },

  resetOrganization(context: ActionContext<IOrganizationState, StoreState>) {
    context.commit("resetOrganizationState");
  },

  async deleteAgentFromOrganization(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { agentId: string; callback: () => void },
  ) {
    let response: ICoreResponse = { success: false };
    try {
      context.commit("enableIsDeletingAgentFromOrganization");
      const agentService = new AgentService();
      response = await agentService.deleteAgent(payload.agentId);
      context.commit("deleteAgentFromOrganization", payload.agentId);
      notificationService.type = NOTIFICATION_TYPES.SUCCESS;
      notificationService.description =
        NOTIFICATION_MESSAGES.AGENT_DELETE_SUCCESS;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    context.commit("disableIsDeletingAgentFromOrganization");
    notificationService.triggerNotification();
    payload.callback();
  },
  async disableAgentTwoFactor(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      organizationUserId: string;
      organizationId: string;
      onSuccess?: () => void;
    },
  ) {
    let response: ICoreResponse = { success: false };
    try {
      const authService = new ProfileAuthService();
      const body: UpdateUserProfileDto = {
        public_id: payload.organizationUserId,
        is_multi_factor_enabled: false,
      };

      response = await authService.updateUserProfile(body);

      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "OTP sent to user's email";

        payload.onSuccess?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  },
  async verifyDisableTwoFactorOtp(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      otp_code: string;
      public_id: string;
      organizationId?: string;
    },
  ) {
    let response: ICoreResponse = { success: false };
    try {
      const authService = new ProfileAuthService();
      const body: VerifyDisableTwoFactorOtpDto = {
        otp_code: payload.otp_code,
        public_id: payload.public_id,
      };

      response = await authService.verifyDisableTwoFactorOtp(body);

      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "Multi-factor authentication disabled";

        if (payload.organizationId) {
          await context.dispatch("fetchOrganization", payload.organizationId);
        }
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  },
  async fetchCreateFormDetails(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      context.commit("enableFetchOrganizationsLoading");
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getFormDetails();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveOrganizations", response.data);
        return response.data;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      context.commit("saveOrganizations", []);
    }
  },
  // *********** Get Organization Form Details ***********
  async fetchOrganizationCreateFormDetails(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      context.commit("setFetchingOrganization", true);
      context.commit("setOrganizationSectors", null);
      context.commit("setOrganizationCurrency", null);
      context.commit("setPiaProviders", null);
      context.commit("setOperationsUsers", []);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getFormDetails();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationSectors", response.data?.sector);
        context.commit("setOrganizationCurrency", response.data?.currency);
        context.commit("setPiaProviders", response.data?.pia_provider);
        context.commit("setOperationsUsers", response.data?.operations_users);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingOrganization", false);
    }
  },

  async updateWebsiteConfiguration(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
    }: {
      payload: UpdateWebsiteConfigDto;
      organizationId: string;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setUpdateWebsiteConfigurationLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.updateWebsiteConfiguration(
        payload,
        organizationId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.data?.message;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setUpdateWebsiteConfigurationLoading", false);
    }
    notificationService.triggerNotification();
  },

  async getWebsiteConfiguration(
    context: ActionContext<IOrganizationState, StoreState>,
    { organizationId }: { organizationId: string },
  ) {
    const response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setWebsiteConfigLoading", true);
      const organizationService = new OrganizationService();
      const config_response =
        await organizationService.getWebsiteConfiguration(organizationId);
      const content_response = await organizationService.getWebsiteContent(
        config_response?.data?.data?.web_slug,
      );
      if (
        content_response.success &&
        content_response.status === StatusCodes.OK
      ) {
        context.commit("setWebsiteConfig", config_response.data?.data);
        context.commit("setWebsiteContent", content_response.data?.data);
      } else {
        throw content_response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setWebsiteConfigLoading", false);
    }
  },

  async updateWebsiteContent(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
    }: { payload: IWebsiteContent; organizationId: string },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setUpdateWebsiteContentLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.updateWebsiteContent(
        payload,
        organizationId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.data?.message;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setUpdateWebsiteContentLoading", false);
    }
    notificationService.triggerNotification();
  },

  // **** Get Organization Passengers ****
  async getOrganizationPassengersList(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: string | { organizationId: string; search?: string },
  ) {
    const organizationId =
      typeof payload === "string" ? payload : payload.organizationId;
    const search = typeof payload === "string" ? undefined : payload.search;
    const isSearchRequest = typeof payload !== "string";

    passengersSearchController?.abort();
    passengersSearchController = undefined;

    if (isSearchRequest && !search?.trim()) {
      context.commit("setPassengers", []);
      context.commit("fetchingPassengers", false);
      return;
    }

    let aborted = false;
    try {
      context.commit("fetchingPassengers", true);

      passengersSearchController = new AbortController();
      const signal = passengersSearchController.signal;

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationPassengers(organizationId, {
          ...(search?.trim() ? { search: search.trim() } : {}),
          signal,
        });

      if (response.aborted) {
        aborted = true;
        return;
      }

      if (response.success && response.status === StatusCodes.OK) {
        const passengers: IPassenger[] = response.data;
        const updatedPassengers = passengers.map((x) => {
          return {
            ...x,
            fullName: x?.first_name + " " + x?.last_name,
          };
        });
        context.commit("setPassengers", updatedPassengers);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      if (!aborted) {
        context.commit("fetchingPassengers", false);
      }
    }
  },

  async getTravelerProfiles(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      organizationId: string;
      search?: string;
      page?: number;
      pageSize?: number;
      requireSearch?: boolean;
      displayDocument?: string;
      passengerType?: string;
    },
  ) {
    const organizationId = payload.organizationId;
    const search = payload.search?.trim() ?? "";
    const requireSearch = payload.requireSearch ?? true;
    const page = payload.page ?? 1;
    const pageSize = payload.pageSize ?? DEFAULT_TRAVELER_PROFILES_PAGE_SIZE;

    travelerProfilesSearchController?.abort();
    travelerProfilesSearchController = undefined;

    if (!search && requireSearch) {
      context.commit("resetTravelerProfiles");
      return;
    }

    let aborted = false;
    try {
      context.commit("setFetchingTravelerProfiles", true);
      context.commit("setTravelerProfilesSearchQuery", search);
      travelerProfilesSearchController = new AbortController();

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getTravelerProfiles(organizationId, {
          page,
          pageSize,
          search,
          displayDocument: payload.displayDocument,
          passengerType: payload.passengerType,
          signal: travelerProfilesSearchController.signal,
        });

      if (response.aborted) {
        aborted = true;
        return;
      }

      if (response.success && response.status === StatusCodes.OK) {
        const paginated = response.data as {
          count?: number;
          next?: string | null;
          previous?: string | null;
          results?: {
            success?: boolean;
            data?: TravelerProfileSummary[];
          };
        };

        const profiles = paginated?.results?.data ?? [];
        const totalRecords = paginated?.count ?? profiles.length;
        const totalPages =
          pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 1;

        const paginationMeta: IPaginationMeta = {
          page_no: page,
          page_size: pageSize,
          has_next: Boolean(paginated?.next),
          total_pages: totalPages,
          total_records: totalRecords,
        };

        context.commit("setTravelerProfiles", profiles);
        context.commit("setTravelerProfilesPaginationMeta", paginationMeta);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      if (!aborted) {
        context.commit("setFetchingTravelerProfiles", false);
      }
    }
  },

  async getOrganizationUsers(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: {
      organizationId: string;
      search?: string;
      page_size?: number;
    },
  ) {
    const organizationId = payload.organizationId;
    const search = payload.search?.trim() ?? "";
    const pageSize = payload.page_size ?? 10;

    organizationUsersSearchController?.abort();
    organizationUsersSearchController = undefined;

    let aborted = false;
    try {
      context.commit("setSearchingOrganizationUsers", true);
      organizationUsersSearchController = new AbortController();

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationUsers(
          organizationId,
          {
            page_no: 1,
            page_size: pageSize,
            search: search || undefined,
          },
          { signal: organizationUsersSearchController.signal },
        );

      if (response.aborted) {
        aborted = true;
        return;
      }

      if (response.success && response.status === StatusCodes.OK) {
        const users = (response.data?.users || []) as IAgentDetails[];
        context.commit("setOrganizationUsers", users);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      if (!aborted) {
        context.commit("setSearchingOrganizationUsers", false);
      }
    }
  },

  resetOrganizationUsers(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    organizationUsersSearchController?.abort();
    organizationUsersSearchController = undefined;
    context.commit("resetOrganizationUsers");
  },

  async getOrganizationPassengerById(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      organizationId,
      passengerIds,
    }: { organizationId: string; passengerIds: string[] },
  ) {
    try {
      context.commit("setFetchingActivePassenger", true);

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationPassengerById(
          organizationId,
          passengerIds,
        );

      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setFetchingActivePassenger", false);

        const passenger: IPassenger[] = response.data;

        context.commit("setActivePassenger", ...passenger);
      } else {
        context.commit("setFetchingActivePassenger", false);
        throw response;
      }
    } catch (exception) {
      context.commit("setFetchingActivePassenger", false);
    }
  },

  async getOrganizationPassengerSetupForm(
    context: ActionContext<IOrganizationState, StoreState>,
    organizationId: string,
  ) {
    try {
      context.commit("fetchingPassengerSetupForm", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationPassengerSetupForm(
          organizationId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const setupForm: OrganizationPassengerSetupForm = response.data;

        context.commit("saveOrganizationPassengerSetupForm", setupForm);
        context.commit("fetchingPassengerSetupForm", false);
      } else {
        context.commit("fetchingPassengerSetupForm", false);
        throw response;
      }
    } catch (error: unknown) {
      context.commit("fetchingPassengerSetupForm", false);
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },

  async getOrganizationPassengerSetupFormV2(
    context: ActionContext<IOrganizationState, StoreState>,
    organizationId: string,
  ) {
    try {
      context.commit("fetchingPassengerSetupForm", true);

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationPassengerSetupFormV2(
          organizationId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const setupForm: OrganizationPassengerSetupFormV2 = response.data;
        context.commit("saveOrganizationPassengerSetupFormV2", setupForm);
        context.commit("fetchingPassengerSetupForm", false);

        const referenceUrls = new Set<string>();

        setupForm.sections?.forEach((section) => {
          section.fields?.forEach((field) => {
            if (
              field.reference_url &&
              !context.state.fetchingReferenceUrls.has(field.reference_url)
            ) {
              referenceUrls.add(field.reference_url);
            }
          });
        });

        setupForm.additional_fields?.forEach((field) => {
          if (
            field.reference_url &&
            !context.state.fetchingReferenceUrls.has(field.reference_url)
          ) {
            referenceUrls.add(field.reference_url);
          }
        });

        referenceUrls.forEach((url) => {
          context.commit("markReferenceUrlFetching", url);
        });

        await Promise.all(
          [...referenceUrls].map(async (url) => {
            try {
              const refResponse: ICoreResponse =
                await organizationService.getReferenceOptions(url);

              if (refResponse.success) {
                context.commit("saveReferenceOptions", {
                  url,
                  options: refResponse.data,
                });
              }
            } catch (err) {
              console.error(
                `Failed to fetch reference options for: ${url}`,
                err,
              );
            }
          }),
        );
      } else {
        context.commit("fetchingPassengerSetupForm", false);
        throw response;
      }
    } catch (error: unknown) {
      context.commit("fetchingPassengerSetupForm", false);
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },

  // **** Create Organization Passenger ****
  async createOrganizationPassenger(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      body,
      organizationId,
    }: {
      body: IPassenger[];
      organizationId: string;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setPassengerActionLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.createPassenger(
        body,
        organizationId,
      );
      if (response.success && response.status === StatusCodes.CREATED) {
        context.commit("addPassenger", {
          passenger: response.data?.passengers[0],
        });
        context.commit("setPassengerActionLoading", false);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.message as string;
        notificationService.triggerNotification();
        return { success: true };
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();

      return {
        success: false,
        errors: exception.errors?.passengers?.[0] || {},
      };
    } finally {
      context.commit("setPassengerActionLoading", false);
    }
  },

  // **** Fetch Passeger By ID **** //
  async fetchPassengerById(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      organizationId,
      passengerId,
    }: { organizationId: string; passengerId: string },
  ) {
    try {
      context.commit("setFetchingActivePassenger", true);

      if (!context.state.passengers?.length) {
        await context.dispatch("getOrganizationPassengersList", organizationId);
      }

      const passenger = context.state.passengers?.find(
        (p) => p.public_id === passengerId,
      );

      if (passenger) {
        context.commit("setActivePassenger", passenger);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description = "Passenger not found.";
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingActivePassenger", false);
    }
  },

  // **** Update Organization Passenger ****
  async updateOrganizationPassenger(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      body,
      organizationId,
    }: {
      body: IPassenger;
      organizationId: string;
    },
  ) {
    let response: ICoreResponse = { success: false };

    try {
      context.commit("setUpdatingPassenger", true);

      const organizationService = new OrganizationService();
      response = await organizationService.updateOrganizationPassenger(
        body,
        organizationId,
      );

      if (response.success && response.status === StatusCodes.OK) {
        const updatedPassenger: IPassenger =
          response.data?.passengers[0] || body;

        context.commit("updatePassenger", {
          passengerId: updatedPassenger.public_id,
          passenger: updatedPassenger,
        });

        context.commit("setActivePassenger", updatedPassenger);

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.message as string;
        notificationService.triggerNotification();

        return { success: true, updatedPassenger };
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();

      return {
        success: false,
        errors: exception.errors?.passengers?.[0] || {},
      };
    } finally {
      context.commit("setUpdatingPassenger", false);
    }
  },

  // **** Delete Organization Passenger ****
  async deleteOrganizationPassengerById(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      organizationId,
      passengerId,
    }: { organizationId: string; passengerId: string },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setPassengerActionLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.deleteOrganizationPassengerById(
        organizationId,
        passengerId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const passengers = context.state.passengers;
        const updatedPassengers = passengers?.filter(
          (item) => item.public_id !== passengerId,
        );
        context.commit("setPassengers", updatedPassengers);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response?.message as string;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setPassengerActionLoading", false);
    }
    notificationService.triggerNotification();
  },

  async websiteUploadContent(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
    }: { payload: ContentUpload; organizationId: string },
  ) {
    let public_id: string | null = null;
    let content_url: string | null = null;
    let response: ICoreResponse = {
      success: false,
    };

    try {
      const organizationService = new OrganizationService();

      response = await organizationService.organizationContentUpload(
        payload,
        organizationId,
      );

      if (response.success && response.status === StatusCodes.OK) {
        public_id = response.data?.data.public_id;
        content_url = response.data?.data.content_url;

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.data?.message;

        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.WEBSITE_CONTENT_UPLOAD,
          payload,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }

    notificationService.triggerNotification();

    return [public_id, content_url];
  },

  async fetchOrganizationDepartments(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    const organizationService = new OrganizationService();
    try {
      context.commit("setIsFetchingOrganizationDepartments", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse =
        await organizationService.getOrganizationDepartments(
          organizationId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationDepartments", response.data);
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_FETCH,
          { organizationId, isActive },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setOrganizationDepartments", []);
    } finally {
      context.commit("setIsFetchingOrganizationDepartments", false);
    }
  },

  async fetchOrganizationCostCenters(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    const organizationService = new OrganizationService();
    try {
      context.commit("setIsFetchingOrganizationCostCenters", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse =
        await organizationService.getOrganizationCostCenters(
          organizationId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationCostCenters", response.data);
        analyticsService.logActionEvent(COST_CENTER_EVENTS.COST_CENTER_FETCH, {
          organizationId,
          isActive,
        });
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setOrganizationCostCenters", []);
    } finally {
      context.commit("setIsFetchingOrganizationCostCenters", false);
    }
  },

  async fetchOrganizationEmployeeGrades(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    const organizationService = new OrganizationService();
    try {
      context.commit("setIsFetchingOrganizationEmployeeGrades", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse =
        await organizationService.getOrganizationEmployeeGrades(
          organizationId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationEmployeeGrades", response.data);
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_FETCH,
          { organizationId, isActive },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setOrganizationEmployeeGrades", []);
    } finally {
      context.commit("setIsFetchingOrganizationEmployeeGrades", false);
    }
  },

  // *********** Get Whatsapp Members ***********
  async getWhatsappMembers(
    context: ActionContext<IOrganizationState, StoreState>,
    payload: { organizationId: string; page?: number },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    const { organizationId, page = 1 } = payload;
    try {
      context.commit("setWhatsappMembersLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.getWhatsappMembers(
        organizationId,
        page,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const paginatedData = response.data?.data ?? response.data;
        const results = paginatedData?.results ?? [];
        const count = paginatedData?.count ?? 0;

        context.commit("setWhatsMembers", {
          results,
          count,
          next: paginatedData?.next ?? null,
          previous: paginatedData?.previous ?? null,
          page,
          pageSize: WHATSAPP_MEMBERS_PAGE_SIZE,
        });
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Whatsapp Members Successfully Fetched";
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.GET_WHATSAPP_MEMBERS,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setWhatsappMembersLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Add Whatsapp Member ***********
  async addWhatsappMember(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
      onSuccessHandler,
    }: {
      payload: AddWhatsappMemberDto;
      organizationId: string;
      onSuccessHandler: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      const updatedPayload = {
        ...payload,
        contact_number: payload.contact_number?.replace(
          /^0(\d{3})\s?(\d{7})$/,
          "92$1$2",
        ),
      };

      context.commit("setWhatsappActionLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.addWhatsappMember(
        updatedPayload,
        organizationId,
      );
      if (response.success && response.status === StatusCodes.CREATED) {
        onSuccessHandler();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Whatsapp member added successfully";
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.ADD_WHATSAPP_MEMBERS,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setWhatsappActionLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Delete Whatsapp Member ***********
  async deleteWhatsappMemberById(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      whatsappMemberId,
      organizationId,
      successHandler,
    }: {
      whatsappMemberId: string;
      organizationId: string;
      successHandler: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setWhatsappActionLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.deleteWhatsappMembers(
        organizationId,
        whatsappMemberId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Whatsapp member is successfully deleted";
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.DELETE_WHATSAPP_MEMBERS,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setWhatsappActionLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Generate OTP For Whatsapp Member ***********
  async generateOTPForWhatsappMember(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      whatsappMemberId,
      organizationId,
      successHandler,
    }: {
      whatsappMemberId: string;
      organizationId: string;
      successHandler: (data: any) => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setGenerateOTPLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.generateOTPForWhatsappMember(
        organizationId,
        whatsappMemberId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler(response.data?.data);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "OTP generated successfully";
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.GENERATE_OTP_WHATAPP_MEMBER,
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setGenerateOTPLoading", false);
      notificationService.triggerNotification();
    }
  },

  // *********** Authorize OTP For Whatsapp Member ***********
  async authorizeOTPForWhatsappMember(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      whatsappMemberId,
      organizationId,
      successHandler,
    }: {
      payload: AuthorizeWhatsppMemberOTPDto;
      whatsappMemberId: string;
      organizationId: string;
      successHandler: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setWhatsappActionLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.authorizeOTPForWhatsappMember(
        payload,
        organizationId,
        whatsappMemberId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Whatsapp member successfully authorized";
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.AUTHORIZE_OTP_WHATAPP_MEMBER,
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setWhatsappActionLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Get Organization Stats ***********
  async getOrganizationStats(
    context: ActionContext<IOrganizationState, StoreState>,
    organizationId,
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setOrganizationStatsLoading", true);
      context.commit("setOrganizationStats", {});
      context.commit("setOrganizationProductivity", {});
      const organizationService = new OrganizationService();
      response = await organizationService.getOrganizationStats(organizationId);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationStats", response.data?.data);
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.FETCH_ORGANIZATION_SNAPSHOT,
          {
            organizationId,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Organization statistics fetched successfully";
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setOrganizationStatsLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Get Organization Productivity ***********
  async getOrganizationProductivity(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
    }: {
      payload: OrganizationProductivityParamsRequestModel;
      organizationId: string;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setOrganizationProductivityLoading", true);
      context.commit("setOrganizationProductivity", {});
      const organizationService = new OrganizationService();
      response = await organizationService.getOrganizationProductivity(
        organizationId,
        payload,
      );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setOrganizationProductivity", response.data?.data);
        analyticsService.logActionEvent(
          ORGANIZATION_ANALYTICS_EVENTS.FETCH_ORGANIZATION_PRODUCTIVITY,
          {
            organizationId,
            ...payload,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          "Organization Productivity fetched successfully";
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setOrganizationProductivityLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Get Currencies ***********
  async getCurrencies(context: ActionContext<IOrganizationState, StoreState>) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setCurrencyLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.getCurrencies();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setCurrencies", response.data?.results?.data);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Currencies Successfully Fetched";
        analyticsService.logActionEvent(
          CURRENCY_CONVERSION_ANALYTICS_EVENTS.CURRENCY_FETCHED,
        );
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setCurrencyLoading", false);
    }
    notificationService.triggerNotification();
  },

  // *********** Organization Documents Upload **************
  async organizationDocumentsUpload(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      organizationId,
      successHandler,
    }: {
      payload: OrganizationContentUpload;
      organizationId: string;
      successHandler: () => void;
    },
  ) {
    const responses = [];

    try {
      context.commit("setDocumentUploadLoading", true);

      const organizationService = new OrganizationService();

      const uploadFiles = Array.prototype.slice.call(payload.content, 0, 2);

      for (const file of uploadFiles) {
        const response = await organizationService.organizationContentUpload(
          {
            ...payload,
            content: file,
          },
          organizationId,
        );

        if (response.success && response.status === StatusCodes.OK) {
          responses.push(response.data?.data);
        } else {
          // Throw an error to stop the loop if any upload fails
          throw response;
        }
      }

      await organizationService.organizationConfigUpdate(
        { documents: { cnic: responses } },
        organizationId,
      );

      const organization = storageService.getItem<Organization>(
        STORAGE_KEYS.ORGANIZATION,
      );

      if (organization) {
        organization.organization_status =
          ORGANIZATION_STATUS.FINANCIAL_PROFILE_PENDING;
        storageService.setItem(
          STORAGE_KEYS.ORGANIZATION,
          stringEncrypt(JSON.stringify(organization)),
        );
      }

      successHandler();
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setDocumentUploadLoading", false);
    }

    return responses;
  },
  async fetchSaasOrganizations(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      context.commit("enableFetchOrganizationsLoading");
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getAllSaasEliteOrganizations();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveSaasOrganizations", response.data?.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("saveSaasOrganizations", []);
    }
  },

  async updateEliteOrganizationStatus(
    context: ActionContext<IOrganizationState, StoreState>,
    { status, organizationId, successHandler },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("setEliteOrganizationStatusUpdateLoading", true);
      const organizationService = new OrganizationService();
      response = await organizationService.updateEliteOrganizationStatus(
        organizationId,
        status,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Status updated successfully";
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setEliteOrganizationStatusUpdateLoading", false);
      notificationService.triggerNotification();
    }
  },
  async getInternalProviders(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      context.commit("setFetchInternalProvidersLoading", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getInternalProviders();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit(
          "setAirblueProviders",
          response.data?.data?.airblue_providers,
        );
        context.commit("setPIAProviders", response.data?.data?.pia_providers);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchInternalProvidersLoading", false);
    }
  },
  async updateFinancialProfileProviders(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      payload,
      successHandler,
    }: {
      payload: UpdateFinancialProfileProviders;
      successHandler: () => void;
    },
  ) {
    try {
      context.commit("setUpdateFinancialProfileProvidersLoading", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.updateFinancialProfileProviders(payload);
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Providers updated successfully";
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setUpdateFinancialProfileProvidersLoading", false);
      notificationService.triggerNotification();
    }
  },
  async createPricing(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      pricing_list,
      financialProfileId,
      callback,
    }: {
      pricing_list: UpdatePricingDto;
      financialProfileId: string;
      callback: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };
    try {
      context.commit("enablePricingFormSubmissionPending");

      const managePricingService = new ManagePricingService();
      response = await managePricingService.createPricing(
        pricing_list,
        financialProfileId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        callback();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.PRICING_CREATE_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    context.commit("disablePricingFormSubmissionPending");
  },
  async updatePricing(
    context: ActionContext<IOrganizationState, StoreState>,
    {
      pricing_list,
      financialProfileId,
      callback,
    }: {
      pricing_list: UpdatePricingDto;
      financialProfileId: string;
      callback: () => void;
    },
  ) {
    let response: ICoreResponse = {
      success: false,
    };

    try {
      context.commit("enablePricingFormSubmissionPending");

      const managePricingService = new ManagePricingService();
      response = await managePricingService.updatePricing(
        pricing_list,
        financialProfileId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        callback();
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.PRICING_UPDATE_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    context.commit("disableUpdateOrganizationLoading");
    notificationService.triggerNotification();
  },

  async fetchPricingForm(
    context: ActionContext<IOrganizationState, StoreState>,
  ) {
    try {
      const managePricingService = new ManagePricingService();
      const response: ICoreResponse =
        await managePricingService.getPricingForm();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("savePricingForm", response.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("savePricingForm", []);
    }
  },
  async getPricingList(
    context: ActionContext<IOrganizationState, StoreState>,
    orgId: string,
  ) {
    try {
      context.commit("enableFetchPricingListLoading");
      const managePricingService = new ManagePricingService();
      const response: ICoreResponse =
        await managePricingService.getPricingList(orgId);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("savePricingList", response.data.pricing_list);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("savePricingList", []);
      context.commit("disableFetchPricingListLoading");
    }
  },
};

export default actions;
